// oxlint-disable-next-line @atlassian/no-restricted-imports
import { parse, isValid, addYears } from 'date-fns';

import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import { getPluginState } from '../../plugin-factory';
import { Cache } from '../utils';
import type { TextPhrase } from '../utils';

import {
	DATE_FORMATS_BY_LOCALE_LIST,
	isSupportedLocale,
	LOCALES_LIST,
} from './date-formats-by-locale';

export class LocaleManager {
	public static readonly LOCALES: Record<"en-US" | "en-GB", Locale> = LOCALES_LIST;

	public static getSelectedLocale(state: EditorState): string {
		const pluginState = getPluginState(state);
		return pluginState?.locale || 'en-US';
	}
}

export class DateParser {
	private static readonly MIN_DATE_LENGTH: number = 3;
	private static readonly MAX_DATE_LENGTH: number = 20;
	private static readonly SHORT_YEAR_CUTOFF = 60;
	private static readonly DATE_FORMATS_BY_LOCALE = DATE_FORMATS_BY_LOCALE_LIST;
	private static readonly REGEXES = {
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		TRAILING_DATE_DELIMITER_COMMA: /[-/.,]$/u,
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		ALL_DATE_DELIMITERS_COMMA: /[-/.,]/gu,
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		ALL_DATE_DELIMITERS: /[-/.]/gu,
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		NON_ALPHABET_CHARS: /[^A-Za-z]/u,
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		ALL_ALPHABET_CHARS: /[A-Za-z]+/gu,
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		DIGITS: /\d/u,
	};

	private static cache = new Cache<{
		date: Date;
		matchedFormat: string;
		trailingDelimiter: string | null;
	} | null>();

	private static isPotentialDateString(input: string): boolean {
		return (
			this.REGEXES.DIGITS.test(input) &&
			input.length >= this.MIN_DATE_LENGTH &&
			input.length <= this.MAX_DATE_LENGTH
		);
	}

	private static containsAmbiguousMonth(input: string): boolean {
		const matches = input.match(this.REGEXES.ALL_ALPHABET_CHARS);
		if (!matches) {
			return false;
		}
		const longestTokenLength = matches.reduce((max, token) => Math.max(max, token.length), 0);
		return longestTokenLength <= 2;
	}

	private static containsYear(format: string): boolean {
		return format.includes('y');
	}

	private static isEnteredDateAfterToday(date: Date): boolean {
		const today = new Date();
		const todayDate = today.getDate();
		const todayMonth = today.getMonth() + 1;

		// If it's the same month and day as today, consider it as "after or equal to" today
		if (todayMonth === date.getMonth() + 1 && todayDate === date.getDate()) {
			return true;
		}

		// Check if today's date is before the entered date (comparing month and day only)
		// Case 1: Today's month is earlier than the entered month
		// Case 2: Same month, but today's day is earlier than the entered day
		return (
			todayMonth < date.getMonth() + 1 ||
			(todayMonth === date.getMonth() + 1 && todayDate < date.getDate())
		);
	}

	private static normaliseYear(date: Date, format: string): Date {
		const year = date.getFullYear();
		const isEnteredDateAfterToday = this.isEnteredDateAfterToday(date);

		if (year < 100) {
			date = addYears(date, year < this.SHORT_YEAR_CUTOFF ? 2000 : 1900);
		} else if (year < 1000) {
			date = addYears(date, 2000);
		} else if (!this.containsYear(format)) {
			// This only applies for partial numeric formats.
			// If the entered date is after today, add 0 years. Otherwise, add 1 year
			// If it's equal to today, add 0 years.
			date = addYears(date, isEnteredDateAfterToday ? 0 : 1);
		}

		return date;
	}

	private static analyzeDelimiters(input: string): {
		hasMixedDelimiters: boolean;
		strippedInput: string;
		trailingDelimiter: string | null;
	} {
		const delimiterMatches = input.match(this.REGEXES.ALL_DATE_DELIMITERS_COMMA) || [];
		const uniqueDelims = new Set(delimiterMatches);
		const hasMixedDelimiters = uniqueDelims.size > 1;
		const trailingCharMatch = input.match(this.REGEXES.TRAILING_DATE_DELIMITER_COMMA);

		let trailingDelimiter: string | null = null;
		let strippedInput = input;
		if (trailingCharMatch) {
			trailingDelimiter = trailingCharMatch[0];
			strippedInput = input.slice(0, -1);
		}

		return {
			hasMixedDelimiters,
			trailingDelimiter,
			strippedInput,
		};
	}

	private static tryAllFormats(
		strippedInput: string,
		trailingDelimiter: string | null,
		locale: string,
	): { date: Date; matchedFormat: string; trailingDelimiter: string | null } | null {
		if (!isSupportedLocale(locale)) {
			return null;
		}
		const dateFnsLocale = LocaleManager.LOCALES[locale];
		const formats = this.DATE_FORMATS_BY_LOCALE[locale];

		for (const format of formats) {
			const parsed = parse(strippedInput, format, new Date(), { locale: dateFnsLocale });
			if (isValid(parsed)) {
				return {
					date: this.normaliseYear(parsed, format),
					matchedFormat: format,
					trailingDelimiter,
				};
			}
		}
		return null;
	}

	public static parse(
		input: string,
		locale: string,
	): { date: Date; matchedFormat: string; trailingDelimiter: string | null } | null {
		if (!this.isPotentialDateString(input) || this.containsAmbiguousMonth(input)) {
			return null;
		}
		const { hasMixedDelimiters, trailingDelimiter, strippedInput } = this.analyzeDelimiters(input);
		if (hasMixedDelimiters) {
			return null;
		}
		return this.cache.get(`${locale}:${input}`, () => {
			return this.tryAllFormats(strippedInput, trailingDelimiter, locale);
		});
	}

	public static extractDelimiterFromFormat(matchedFormat: string): string {
		return matchedFormat.match(this.REGEXES.NON_ALPHABET_CHARS)?.[0] ?? ' ';
	}
}

export class DateUtils {
	public static getDateOnlyUtcTimestamp(date: Date): number {
		return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
	}

	public static canInsertDateNode(state: EditorState, pos: number): boolean {
		const dateType: NodeType | undefined = state?.schema?.nodes?.date;
		if (!dateType) {
			return false;
		}
		const $pos = state.doc.resolve(pos);
		return $pos.parent.canReplaceWith($pos.index(), $pos.index(), dateType);
	}
}
export class CursorPhraseUtils {
	public static sortByDescendingRangeLength(phrases: TextPhrase[]): TextPhrase[] {
		return phrases.slice().sort((a, b) => {
			const aLength = a.to - a.from;
			const bLength = b.to - b.from;
			return bLength - aLength;
		});
	}

	public static findFirstValidDatePhrase(phrases: TextPhrase[], locale: string): {
        phrase: TextPhrase;
        result: {
            date: Date;
            matchedFormat: string;
            trailingDelimiter: string | null;
        };
    } | null {
		for (const phrase of phrases) {
			const result = DateParser.parse(phrase.phrase, locale);
			if (result) {
				return { phrase, result };
			}
		}
		return null;
	}
}
